import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { LanguageSchema } from '../common/language.js';
import { CurrencyCodeSchema } from '../common/currency.js';
import { ImportFormatSchema } from '../import-export/envelopes.js';
import { IanaZoneSchema } from '../common/zone.js';
import { WeekStartSchema } from '../common/period.js';
// Request schemas for the SYSADMIN platform surface (/api/platform/projects/*,
// ADR-018 Zone 2). Consumed by the backend (Fastify type provider validates the
// body) and by the frontend mutations as the wire DTO type (the create wizard +
// edit drawers keep a wizard-LOCAL RHF form schema and map to these on submit —
// the RoleCreatePage pattern; ADR-008 + CONVENTIONS §Schemas: form validation is
// never stricter than the backend, which re-validates here).
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
// Trial length presets — create sends a DURATION; the backend stamps
// trial_expires_at = now + length. (Extend uses an absolute date on update.)
export const TrialLengthSchema = z.enum(['1w', '1m', '3m', '6m']);
// A tier the project INCLUDES (ADR-055 — every type; a site type may take only one of
// them): a prepaid (embedded/demo) project buys `seats` for it (null reads as 0); a
// subscription (esco) includes it WITHOUT seats — `seats: null` (a number is
// ESCO_HAS_NO_SEATS).
export const ProjectTierInputSchema = z
    .object({ tier_code: z.string().min(1).max(16), seats: z.number().int().min(0).nullable() })
    .strict();
// The tiers a project includes — each ONCE (the guards read one row per tier; a second would be the one stored).
const ProjectTiersSchema = z.array(ProjectTierInputSchema).superRefine((tiers, ctx) => {
    const seen = new Set();
    tiers.forEach((tier, i) => {
        if (seen.has(tier.tier_code))
            ctx.addIssue({ code: 'custom', path: [i, 'tier_code'], message: 'TIER_LISTED_TWICE' });
        seen.add(tier.tier_code);
    });
});
// POST /api/platform/projects — atomic create (project + countries + tiers).
export const CreatePlatformProjectRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(32), // the immutable code (CI-unique)
    display_name: z.string().trim().min(1).max(64),
    type: z.enum(['esco', 'embedded', 'demo']),
    description: z.string().trim().max(1000), // '' allowed
    color_primary: z.string().regex(HEX_COLOR),
    color_on_primary: z.string().regex(HEX_COLOR),
    mother_company: z.string().trim().max(80), // '' allowed — the PAYING party
    // The OPERATING party — the voice of every project-scoped email (ADR-070).
    service_provider: z.string().trim().max(80),
    service_provider_address: z.string().trim().max(300),
    service_provider_support_email: z.union([z.string().trim().email(), z.literal('')]),
    countries: z.array(z.string().length(2)).min(1),
    default_country_iso_code: z.string().length(2).nullable(),
    // The project's zone and week start (ADR-082 §3) — written ONCE here; the wizard pre-fills both from the
    // default country (else the first listed country), the sysadmin's to override; a project admin edits them
    // later in Regional defaults. Required: a project always has a zone, and its records' day is cut in it.
    timezone_iana: IanaZoneSchema,
    week_start: WeekStartSchema,
    // The base currency — pre-filled by the wizard from the default country's usual one, the sysadmin's to override.
    default_currency: CurrencyCodeSchema,
    default_language: LanguageSchema,
    has_invoicing: z.boolean(),
    has_field_force: z.boolean(),
    has_vehicles: z.boolean(),
    tiers: ProjectTiersSchema, // the tiers it includes; esco without seats
    is_trial: z.boolean(),
    trial_length: TrialLengthSchema.nullable(), // required when is_trial (refined)
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.default_country_iso_code && !v.countries.includes(v.default_country_iso_code)) {
        ctx.addIssue({ code: 'custom', path: ['default_country_iso_code'], message: 'DEFAULT_COUNTRY_NOT_WHITELISTED' });
    }
    if (v.is_trial && !v.trial_length) {
        ctx.addIssue({ code: 'custom', path: ['trial_length'], message: 'TRIAL_LENGTH_REQUIRED' });
    }
    if (v.type === 'esco' && v.tiers.some((t) => t.seats !== null)) {
        ctx.addIssue({ code: 'custom', path: ['tiers'], message: 'ESCO_HAS_NO_SEATS' });
    }
});
// PATCH /api/platform/projects/:projectId — partial update. Every field optional;
// present ⇒ change. `countries` + `tiers` are REPLACE-SETs (present ⇒ full replace,
// like roles' permissions). Trial extend/convert ride here (is_trial +
// trial_expires_at). The project CODE + TYPE are immutable after creation (absent).
// Cross-field checks (default-country ∈ countries, esco-has-no-seats) run in the
// service against the merged current+incoming state. No zone field: the zone is
// edited on the project's own settings surface; a country change here re-derives
// it from the new country when that country has one (ADR-082 §3).
// ── license-tier catalogue management (global, sysadmin) ───────────────────────
// The vendor-owned tier catalogue (ADR-055). Add a tier + rename it; the CODE and
// the power-system CAP are IMMUTABLE after create — the cap is a universal
// activation invariant (a Simple site ≤ 1 power system), so changing it would
// retroactively re-scope every site of that tier. Reorder + delete are out of scope
// (deletion is FK-guarded — site_types + the projects' included tiers reference the code).
export const CreateLicenseTierRequestSchema = z
    .object({
    code: z.string().trim().min(1).max(16).regex(/^[a-z][a-z0-9_]*$/, 'TIER_CODE_FORMAT'),
    name: z.string().trim().min(1).max(32),
    max_active_power_systems: z.number().int().min(1).nullable(),
})
    .strict();
export const UpdateLicenseTierRequestSchema = z.object({ ...stampedBody, name: z.string().trim().min(1).max(32) }).strict();
// GET /api/platform/projects/export — the current-view export query (ADR-041/045).
// The frontend passes its live filters + search + UI language; the service re-applies
// them atop the sysadmin-visible set (all projects) and streams XLSX/CSV. status/type
// arrive comma-joined (buildExportParams) → split back to arrays.
const csvFilter = z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(z.string().max(32)).max(50).optional());
export const ExportProjectsQuerySchema = z
    .object({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
    status: csvFilter,
    type: csvFilter,
    q: z.string().trim().max(255).optional(),
})
    .strict();
export const UpdatePlatformProjectRequestSchema = z
    .object({
    ...stampedBody,
    display_name: z.string().trim().min(1).max(64).optional(),
    description: z.string().trim().max(1000).optional(),
    mother_company: z.string().trim().max(80).optional(),
    service_provider: z.string().trim().max(80).optional(),
    service_provider_address: z.string().trim().max(300).optional(),
    service_provider_support_email: z.union([z.string().trim().email(), z.literal('')]).optional(),
    color_primary: z.string().regex(HEX_COLOR).optional(),
    color_on_primary: z.string().regex(HEX_COLOR).optional(),
    has_invoicing: z.boolean().optional(),
    has_field_force: z.boolean().optional(),
    has_vehicles: z.boolean().optional(),
    countries: z.array(z.string().length(2)).min(1).optional(),
    default_country_iso_code: z.string().length(2).nullable().optional(),
    default_currency: CurrencyCodeSchema.optional(),
    default_language: LanguageSchema.optional(),
    tiers: ProjectTiersSchema.optional(),
    is_trial: z.boolean().optional(),
    trial_expires_at: z.string().datetime().nullable().optional(),
})
    .strict();
